import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box } from "@mui/material";

// Reused from the MAC Leader "My Allocations" screen — same data shape,
// same edit/wizard flow. PMO just sees every MAC instead of one.
import AllocationsHeader from "../../../MACLeader/components/MyAllocations/AllocationsHeader";
import StatsStrip from "../../../MACLeader/components/MyAllocations/StatsStrip";
import SOWFilterBar from "../../../MACLeader/components/MyAllocations/SOWFilterBar";
import AllocationsTable from "../../../MACLeader/components/MyAllocations/AllocationsTable";
import AllocationWizard from "../../../MACLeader/components/MyAllocations/AllocationWizard";
import ExceptionsDrawer from "../../../MACLeader/components/MyAllocations/ExceptionsDrawer";

// Reused from Position ID Management — already does exactly the
// "MAC list, with SOWs nested under the selected MAC" sidebar pattern.
import MACFilter from "../PositionIDManagement/MACFilter";

import { getMyAllocations } from "../../../../../api/macLeader";
import { getMacsWithSows } from "../../../../../api/macs";
import PageInfo from "../../../../../components/common/PageInfo";

const AllAllocations = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [macs, setMacs] = useState([]);
  const [macsLoading, setMacsLoading] = useState(true);

  const [selectedMac, setSelectedMac] = useState(null); // null = All MACs
  const [selectedSow, setSelectedSow] = useState(null); // null = All SOWs

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [posTypeFilter, setPosTypeFilter] = useState(null);
  const [associateTypeFilter, setAssociateTypeFilter] = useState(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [exceptionsOpen, setExceptionsOpen] = useState(false);
  const [exceptionsRow, setExceptionsRow] = useState(null);

  // GET /api/allocation-requests — for a PMO/SCG user the backend applies
  // no MAC filter at all (that only happens for MAC_LEADER), so this already
  // returns every MAC's allocations. No new endpoint needed for this screen.
  const fetchAllocations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMyAllocations();
      setRows(data);
    } catch (err) {
      console.error("Failed to load allocations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  // GET /api/macs — all MACs with nested SOWs, drives the sidebar
  // and lets us resolve which MAC a given allocation row belongs to
  // (the allocation DTO only carries MacName/SowId, not MacId).
  useEffect(() => {
    setMacsLoading(true);
    getMacsWithSows()
      .then(setMacs)
      .catch((err) => console.error("Failed to load MACs:", err))
      .finally(() => setMacsLoading(false));
  }, []);

  const sowIdToMacId = useMemo(() => {
    const map = {};
    macs.forEach((mac) => {
      (mac.sows ?? []).forEach((sow) => {
        map[sow.sowId] = mac.macId;
      });
    });
    return map;
  }, [macs]);

  const selectedMacName = useMemo(
    () => macs.find((m) => m.macId === selectedMac)?.macName ?? null,
    [macs, selectedMac]
  );

  const summary = {
    // Only show a single MAC badge when one is actually selected —
    // StatsStrip already hides the badge when macName is falsy.
    macName: selectedMacName,
    allocatedHc: 0,
    billed: 0,
    unbilled: 0,
    pending: 0,
    utilisation: null,
  };

  const filteredRows = rows.filter((row) => {
    const rowMacId = sowIdToMacId[row.sowId] || (row.sourceSowId ? sowIdToMacId[row.sourceSowId] : null);
    const matchesMac = !selectedMac || rowMacId === selectedMac;
    const matchesSow = !selectedSow || row.sowId === selectedSow || row.sourceSowId === selectedSow;
    const matchesStatus =
      !statusFilter ||
      row.status === statusFilter ||
      (statusFilter === "SUBMITTED" && row.status === "MOVE_PENDING");
    const matchesPosType = !posTypeFilter || row.positionType === posTypeFilter;
    const matchesAssocType =
      !associateTypeFilter ||
      (associateTypeFilter === "CONTRACTOR" ? row.isContractor : !row.isContractor);
    const matchesSearch =
      !search.trim() ||
      row.associateName?.toLowerCase().includes(search.toLowerCase()) ||
      row.positionCode?.toLowerCase().includes(search.toLowerCase());
    return matchesMac && matchesSow && matchesStatus && matchesPosType && matchesAssocType && matchesSearch;
  });

  // Recompute the stats strip off the MAC-filtered (but not status/search
  // filtered) set, same convention as MyAllocations.
  const macScopedRows = rows.filter((row) => {
    const rowMacId = sowIdToMacId[row.sowId] || (row.sourceSowId ? sowIdToMacId[row.sourceSowId] : null);
    return !selectedMac || rowMacId === selectedMac;
  });
  summary.allocatedHc = macScopedRows.filter((r) => r.status === "ACTIVE").length;
  summary.billed = macScopedRows.filter(
    (r) => r.status === "ACTIVE" && r.positionType === "BIL"
  ).length;
  summary.unbilled = macScopedRows.filter(
    (r) => r.status === "ACTIVE" && r.positionType === "UBL"
  ).length;
  summary.pending = macScopedRows.filter((r) =>
    ["SUBMITTED", "PMO_APPROVED", "MOVE_PENDING"].includes(r.status)
  ).length;

  const normalizeRow = (row) => ({
    ...row,
    associate: {
      id: row.associateId,
      fullName: row.associateName,
      levelCode: row.associateLevelCode || row.levelCode,
      skills: [],
    },
    position: {
      positionCode: row.positionCode,
      roleTitle: row.roleTitle,
      sowId: row.sowId,
      sowName: row.sowName,
      macName: row.macName,
    },
    fte: row.fte,
    startDate: row.startDate,
    endDate: row.endDate,
    skills: row.skills,
    deployedSkillNames: row.deployedSkillNames ?? [],
  });

  const handleEdit = (row) => {
    setEditRow(normalizeRow(row));
    setReadOnly(false);
    setWizardOpen(true);
  };
  const handleView = (row) => {
    setEditRow(normalizeRow(row));
    setReadOnly(true);
    setWizardOpen(true);
  };
  const handleClose = () => {
    setWizardOpen(false);
    setEditRow(null);
    setReadOnly(false);
  };
  const handleExceptions = (row) => {
    setExceptionsRow(normalizeRow(row));
    setExceptionsOpen(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* No "+ New Allocation Request" here — PMO reviews/edits existing
          requests across MACs; submission stays with the MAC Leader flow. */}
      <AllocationsHeader title="All Allocations" showNewRequest={false} />

      <StatsStrip macName={summary.macName} summary={summary} loading={loading} />

      <PageInfo message="View and edit allocations across every MAC" />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <MACFilter
          macs={macs}
          loading={macsLoading}
          selectedMAC={selectedMac}
          selectedSOW={selectedSow}
          onSelectMAC={setSelectedMac}
          onSelectSOW={setSelectedSow}
        />

        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <SOWFilterBar
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            posTypeFilter={posTypeFilter}
            onPosTypeChange={setPosTypeFilter}
            associateTypeFilter={associateTypeFilter}
            onAssociateTypeChange={setAssociateTypeFilter}
          />

          <AllocationsTable
            rows={filteredRows}
            loading={loading}
            onEdit={handleEdit}
            onView={handleView}
            onEnded={fetchAllocations}
            onExceptions={handleExceptions}
            onDeleted={fetchAllocations}
            showDelete
            showMacColumn
          />
        </Box>
      </Box>

      <AllocationWizard
        open={wizardOpen}
        onClose={handleClose}
        onSubmitted={fetchAllocations}
        editRow={editRow}
        readOnly={readOnly}
      />
      <ExceptionsDrawer
        open={exceptionsOpen}
        onClose={() => {
          setExceptionsOpen(false);
          setExceptionsRow(null);
        }}
        row={exceptionsRow}
      />
    </Box>
  );
};

export default AllAllocations;